import { NgModule } from '@angular/core';
import { RouterModule, Routes, PreloadAllModules } from '@angular/router';
import { LayoutComponent } from './components/layout/layout.component';
import { LoggedInGuard } from './core/guards/logged-in.guard';
import { AuthorizationGuard } from './core/guards/authorization.guard';
import { MfaRequiredGuard } from './core/guards/mfa-required.guard';
import { ClientPortalGuard } from './core/guards/client-portal.guard';
import { InternalOnlyGuard } from './core/guards/internal-only.guard';

import { LoginComponent } from './pages/auth/login/login.component';
import { MfaVerifyComponent } from './pages/auth/mfa-verify/mfa-verify.component';
import { MfaSetupComponent } from './pages/auth/mfa-setup/mfa-setup.component';
import { MfaRequiredComponent } from './pages/auth/mfa-required/mfa-required.component';
import { ForgotPasswordComponent } from './pages/auth/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './pages/auth/reset-password/reset-password.component';

const routes: Routes = [
  // Auth routes — eager (visitadas uma vez, não valem lazy loading)
  { path: 'login',           component: LoginComponent },
  { path: 'mfa-verify',      component: MfaVerifyComponent },
  { path: 'mfa-required',    component: MfaRequiredComponent, canActivate: [LoggedInGuard] },
  { path: 'forgot-password', component: ForgotPasswordComponent },
  { path: 'reset-password',  component: ResetPasswordComponent },

  // Client portal — completely separate module tree from internal shell
  {
    path: 'portal',
    loadChildren: () => import('./portal/portal.module').then(m => m.PortalModule),
    canActivate: [LoggedInGuard, MfaRequiredGuard, ClientPortalGuard],
  },

  // Internal app shell
  {
    path: '',
    component: LayoutComponent,
    canActivate: [LoggedInGuard, MfaRequiredGuard, InternalOnlyGuard],
    children: [
      {
        path: 'dashboard',
        loadChildren: () => import('./pages/dashboard/dashboard.module').then(m => m.DashboardModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'dashboard:visualizar' },
      },
      {
        path: 'categories',
        loadChildren: () => import('./pages/categories/categories.module').then(m => m.CategoriesModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'categorias:visualizar' },
      },
      {
        path: 'transactions',
        loadChildren: () => import('./pages/transactions/transactions.module').then(m => m.TransactionsModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'lancamentos:visualizar' },
      },
      {
        path: 'clients',
        loadChildren: () => import('./pages/clients/clients.module').then(m => m.ClientsModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'clientes:visualizar' },
      },
      {
        path: 'collaborators',
        loadChildren: () => import('./pages/collaborators/collaborators.module').then(m => m.CollaboratorsModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'service-providers:visualizar' },
      },
      {
        path: 'employees',
        loadChildren: () => import('./pages/collaborators/collaborators.module').then(m => m.CollaboratorsModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'funcionarios:visualizar', tipo: 'funcionario' },
      },
      {
        path: 'service-providers',
        loadChildren: () => import('./pages/collaborators/collaborators.module').then(m => m.CollaboratorsModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'service-providers:visualizar', tipo: 'service-provider' },
      },
      {
        path: 'birthdays',
        loadChildren: () => import('./pages/birthdays/birthdays.module').then(m => m.BirthdaysModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'aniversariantes:visualizar' },
      },
      {
        path: 'invoices',
        loadChildren: () => import('./pages/invoices/invoices.module').then(m => m.InvoicesModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'faturas:visualizar' },
      },
      {
        path: 'accounts-payable',
        loadChildren: () => import('./pages/accounts-payable/accounts-payable.module').then(m => m.AccountsPayableModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'contas-a-pagar:visualizar' },
      },
      {
        path: 'users',
        loadChildren: () => import('./pages/users/users.module').then(m => m.UsersModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'usuarios:visualizar' },
      },
      {
        path: 'accounts',
        loadChildren: () => import('./pages/accounts/accounts.module').then(m => m.AccountsModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'contas:visualizar' },
      },
      {
        path: 'credit-card',
        loadChildren: () => import('./pages/credit-card/credit-card.module').then(m => m.CreditCardModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'cartao-credito:visualizar' },
      },
      {
        path: 'roles',
        loadChildren: () => import('./pages/roles/roles.module').then(m => m.RolesModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'configuracoes:visualizar' },
      },
      {
        path: 'reports',
        loadChildren: () => import('./pages/reports/reports.module').then(m => m.ReportsModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'relatorios:visualizar' },
      },
      {
        path: 'operation-reports',
        loadChildren: () => import('./pages/operation-reports/operation-reports.module').then(m => m.OperationReportsModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'client:report:visualizar' },
      },
      {
        path: 'settings',
        loadChildren: () => import('./pages/settings/settings.module').then(m => m.SettingsModule),
        canActivate: [AuthorizationGuard],
      },
      {
        path: 'logs',
        loadChildren: () => import('./pages/logs/logs.module').then(m => m.LogsModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'configuracoes:visualizar' },
      },
      {
        path: 'test-runner',
        loadChildren: () => import('./pages/test-runner/test-runner.module').then(m => m.TestRunnerModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'configuracoes:visualizar' },
      },
      {
        path: 'sql-editor',
        loadChildren: () => import('./pages/sql-editor/sql-editor.module').then(m => m.SqlEditorModule),
        canActivate: [AuthorizationGuard],
        data: { permission: 'sql_editor:visualizar' },
      },
      { path: 'mfa-setup', loadChildren: () => import('./pages/auth/mfa-setup/mfa-setup.module').then(m => m.MfaSetupModule), canActivate: [LoggedInGuard] },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    ],
  },

  { path: '**', redirectTo: 'dashboard' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
